//----- Component --------------------------------------------------------/
// Developer : Daiana Curcio/Nicolas Pascucci
// Creation Date : 01/02/2024
// Version : 2
// Description : Pantalla de carga de los pedidos por parte de las plantas
//------Changes ----------------------------------------------------------//
// Change Date : 04/11/2026
// Change by :
// Change description: Rediseño en tabla y guardado semanal conservando lo asignado
//========================================================================/

import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import "moment/locale/es";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Container,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SettingsIcon from "@mui/icons-material/Settings";
import Navbar from "../Navbar";
import Title from "../Title";
import ConfigDias from "./ConfigDias";
import { UrlApi } from "../../services/apirest";
import { logEventos } from "../../services/services";

const COLOR = {
  encabezado: { fondo: "#E3F2FD", borde: "#1976D2" },
  pedido: { fondo: "#E8F5E9", borde: "#2E7D32", celda: "#C8E6C9" },
  resumen: { fondo: "#FFF8E1", borde: "#F9A825" },
};

const NOMBRES_DIAS_CONFIG = {
  lunes: "lunes",
  martes: "martes",
  miercoles: "miércoles",
  jueves: "jueves",
  viernes: "viernes",
};

const panelSx = (color) => ({
  border: "1px solid",
  borderColor: "divider",
  borderTop: `3px solid ${color.borde}`,
  borderRadius: 1,
  bgcolor: color.fondo,
  px: 1.5,
  py: 1,
});

const headerCellSx = {
  py: 0.5,
  px: 1,
  fontSize: 12,
  fontWeight: 700,
  textAlign: "center",
  bgcolor: COLOR.pedido.celda,
  whiteSpace: "nowrap",
};

const bodyCellSx = {
  py: 0.5,
  px: 0.75,
  fontSize: 13,
  textAlign: "center",
};

const tituloFilaSx = {
  ...bodyCellSx,
  textAlign: "left",
  fontWeight: 700,
  whiteSpace: "nowrap",
};

const crearSemana = (inicio) =>
  [0, 1, 2, 3, 4, 5].map((dias) => {
    const fecha = inicio.clone().add(dias, "days");

    return {
      fecha: fecha.format("YYYY-MM-DD"),
      nombre: fecha.format("ddd"),
      dia: fecha.format("DD/MM"),
    };
  });

const crearValoresVacios = (semana) =>
  semana.reduce((resultado, dia) => {
    resultado[dia.fecha] = { butano: "", propano: "" };
    return resultado;
  }, {});

const sumarCantidad = (semana, valores, producto) =>
  semana.reduce(
    (total, dia) => total + (Number(valores[dia.fecha]?.[producto]) || 0),
    0
  );

function SolicitudSucursal() {
  const esAdmin = localStorage.getItem("role") === "admin";
  const sucursalUsuario = localStorage.getItem("sucursal");

  const [mostrarConfig, setMostrarConfig] = useState(false);
  const [sucursales, setSucursales] = useState([]);
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState(null);
  const [inicioSemana, setInicioSemana] = useState(() => moment().day(8));
  const [valores, setValores] = useState({});
  const [valoresOriginales, setValoresOriginales] = useState({});
  const [solicitudesPorFecha, setSolicitudesPorFecha] = useState({});
  const [cargando, setCargando] = useState(false);
  const [configuracion, setConfiguracion] = useState();
  const [errorCarga, setErrorCarga] = useState("");
  const [errorGuardado, setErrorGuardado] = useState(null);
  const [mensaje, setMensaje] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [recarga, setRecarga] = useState(0);
  const guardandoRef = useRef(false);

  const idSucursal = esAdmin ? sucursalSeleccionada?.id : sucursalUsuario;
  const inicioTexto = inicioSemana.format("YYYY-MM-DD");

  const semana = useMemo(() => crearSemana(inicioSemana), [inicioTexto]);
  const periodoTexto =
    inicioSemana.format("DD/MM") +
    " al " +
    inicioSemana.clone().add(5, "days").format("DD/MM/YYYY");

  useEffect(() => {
    logEventos(
      "Acceso al menú pedidos semanales",
      "Pedidos Semanales",
      "Hacer pedidos semanales de butano y propano",
      localStorage.getItem("user")
    );
  }, []);

  useEffect(() => {
    let vigente = true;

    fetch(UrlApi + "/sucursales/tipoPlanta")
      .then((res) => res.json())
      .then((data) => {
        if (vigente) setSucursales(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error(err);
        if (vigente) setErrorCarga("No se pudo cargar la lista de plantas.");
      });

    return () => {
      vigente = false;
    };
  }, []);

  useEffect(() => {
    let vigente = true;

    fetch(UrlApi + "/config_diashabilitados/ultimaRow")
      .then((res) => res.json())
      .then((data) => {
        if (vigente) setConfiguracion(Array.isArray(data) ? data[0] || null : null);
      })
      .catch((err) => {
        console.error(err);
        if (vigente) setConfiguracion(null);
      });

    return () => {
      vigente = false;
    };
  }, [mostrarConfig]);

  useEffect(() => {
    const vacios = crearValoresVacios(semana);

    setErrorGuardado(null);
    setSolicitudesPorFecha({});
    setValores(vacios);
    setValoresOriginales(vacios);

    if (!idSucursal) return undefined;

    let vigente = true;
    setCargando(true);
    setErrorCarga("");

    const ultimaFecha = semana[semana.length - 1].fecha;

    fetch(
      UrlApi +
      "/solicitudes_abastecimiento/existentes/" +
      idSucursal +
      "/" +
      semana[0].fecha +
      "/" +
      ultimaFecha
    )
      .then(async (res) => {
        const data = await res.json().catch(() => null);

        if (!res.ok || !Array.isArray(data)) {
          throw new Error("Respuesta inválida al cargar solicitudes");
        }

        if (!vigente) return;

        const nuevosValores = crearValoresVacios(semana);
        const porFecha = {};

        data.forEach((solicitud) => {
          const fecha = moment(solicitud.para_fecha).format("YYYY-MM-DD");

          if (!nuevosValores[fecha]) return;

          nuevosValores[fecha] = {
            butano: solicitud.butano === null ? "" : String(solicitud.butano),
            propano: solicitud.propano === null ? "" : String(solicitud.propano),
          };
          porFecha[fecha] = solicitud;
        });

        setValores(nuevosValores);
        setValoresOriginales(nuevosValores);
        setSolicitudesPorFecha(porFecha);
      })
      .catch((err) => {
        console.error(err);
        if (vigente) {
          setErrorCarga(
            "No se pudieron cargar los pedidos de la semana. Recargá la pantalla o avisá a Sistemas."
          );
        }
      })
      .finally(() => {
        if (vigente) setCargando(false);
      });

    return () => {
      vigente = false;
    };
  }, [idSucursal, semana, recarga]);

  const hoyClave = moment()
    .locale("es")
    .format("dddd")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  const diaHabilitado = Boolean(configuracion && configuracion[hoyClave] === 1);
  const puedeEditar =
    Boolean(idSucursal) && !errorCarga && (esAdmin || diaHabilitado);

  const diasHabilitadosTexto = configuracion
    ? Object.keys(NOMBRES_DIAS_CONFIG)
      .filter((clave) => configuracion[clave] === 1)
      .map((clave) => NOMBRES_DIAS_CONFIG[clave])
      .join(", ")
    : "";

  const hayCambios = semana.some(
    (dia) =>
      valores[dia.fecha]?.butano !== valoresOriginales[dia.fecha]?.butano ||
      valores[dia.fecha]?.propano !== valoresOriginales[dia.fecha]?.propano
  );

  const obtenerAsignados = (fecha, producto) => {
    const solicitud = solicitudesPorFecha[fecha];
    if (!solicitud) return 0;

    const saldo =
      producto === "butano" ? solicitud.viaje_butano : solicitud.viaje_propano;

    return (Number(solicitud[producto]) || 0) - (Number(saldo) || 0);
  };

  const totalButano = sumarCantidad(semana, valores, "butano");
  const totalPropano = sumarCantidad(semana, valores, "propano");
  const asignadosButano = semana.reduce(
    (total, dia) => total + Math.max(obtenerAsignados(dia.fecha, "butano"), 0),
    0
  );
  const asignadosPropano = semana.reduce(
    (total, dia) => total + Math.max(obtenerAsignados(dia.fecha, "propano"), 0),
    0
  );

  const cambiarValor = (fecha, producto, valor) => {
    setErrorGuardado(null);
    setValores((anteriores) => ({
      ...anteriores,
      [fecha]: {
        ...anteriores[fecha],
        [producto]: valor.replace(/\D/g, "").slice(0, 3),
      },
    }));
  };

  const guardarSemana = async () => {
    if (guardandoRef.current || !puedeEditar) return;

    guardandoRef.current = true;
    setGuardando(true);
    setErrorGuardado(null);

    try {
      const res = await fetch(
        UrlApi + "/solicitudes_abastecimiento/guardar-semana",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id_sucursal: idSucursal,
            lunes: semana[0].fecha,
            usuario: localStorage.getItem("user"),
            dias: semana.map((dia) => ({
              fecha: dia.fecha,
              butano: valores[dia.fecha].butano,
              propano: valores[dia.fecha].propano,
            })),
          }),
        }
      );

      const data = await res.json().catch(() => null);

      if (!res.ok || !data?.ok) {
        setErrorGuardado({
          mensaje: data?.mensaje || "No se pudieron guardar los pedidos.",
          problemas: Array.isArray(data?.problemas) ? data.problemas : [],
        });
        return;
      }

      logEventos(
        "Guardar solicitud",
        "Pedidos Semanales",
        "Se guardó la semana " + semana[0].fecha + " de la planta " + idSucursal,
        localStorage.getItem("user")
      );

      setMensaje("Los pedidos se guardaron correctamente.");
      setRecarga((valor) => valor + 1);
    } catch (err) {
      console.error(err);
      setErrorGuardado({
        mensaje: "No se pudo conectar con el servidor. Intentá nuevamente.",
        problemas: [],
      });
    } finally {
      guardandoRef.current = false;
      setGuardando(false);
    }
  };

  if (mostrarConfig) {
    return (
      <Container maxWidth="lg">
        <Title
          titulo="Configuración de días habilitados"
          handleClose={() => setMostrarConfig(false)}
        />
        <ConfigDias
          configDias={mostrarConfig}
          onClose={() => setMostrarConfig(false)}
        />
      </Container>
    );
  }

  const nombrePlantaUsuario = sucursales.find(
    (sucursal) => String(sucursal.id) === String(sucursalUsuario)
  )?.nombre;

  return (
    <>
      <Navbar />

      <Box
        sx={{
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          bgcolor: "grey.50",
          minHeight: "calc(100vh - 64px)",
        }}
      >
        <Box
          sx={{
            ...panelSx(COLOR.encabezado),
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            columnGap: 2,
            rowGap: 1,
          }}
        >
          <Typography sx={{ fontWeight: 700 }}>Pedidos semanales</Typography>

          {esAdmin ? (
            <Autocomplete
              size="small"
              disableClearable
              sx={{ width: 280, bgcolor: "white" }}
              options={sucursales}
              value={sucursalSeleccionada}
              autoHighlight
              noOptionsText="Planta inexistente"
              isOptionEqualToValue={(opcion, valor) => opcion.id === valor.id}
              getOptionLabel={(sucursal) =>
                sucursal.id + " - " + sucursal.nombre
              }
              onChange={(event, valor) => setSucursalSeleccionada(valor)}
              renderInput={(params) => <TextField {...params} label="Planta" />}
            />
          ) : (
            <Typography variant="body2">
              Planta: <b>{nombrePlantaUsuario || sucursalUsuario || "Sin asignar"}</b>
            </Typography>
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {esAdmin ? (
              <Tooltip title="Semana anterior">
                <IconButton
                  size="small"
                  onClick={() =>
                    setInicioSemana((fecha) => fecha.clone().subtract(7, "days"))
                  }
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}

            <Typography variant="body2">
              Semana: <b>{periodoTexto}</b>
            </Typography>

            {esAdmin ? (
              <Tooltip title="Semana siguiente">
                <IconButton
                  size="small"
                  onClick={() =>
                    setInicioSemana((fecha) => fecha.clone().add(7, "days"))
                  }
                >
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {esAdmin ? (
            <Tooltip title="Configurar días habilitados">
              <IconButton size="small" onClick={() => setMostrarConfig(true)}>
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>

        {errorCarga ? <Alert severity="error">{errorCarga}</Alert> : null}

        {esAdmin && !idSucursal ? (
          <Alert severity="info">Elegí una planta para ver o cargar sus pedidos.</Alert>
        ) : null}

        {!esAdmin && !sucursalUsuario ? (
          <Alert severity="error">
            Tu usuario no tiene una planta asignada. Avisá a Sistemas.
          </Alert>
        ) : null}

        {!esAdmin && sucursalUsuario && configuracion !== undefined && !diaHabilitado ? (
          <Alert severity="warning">
            Hoy no está habilitada la carga de pedidos.
            {diasHabilitadosTexto
              ? " Días habilitados: " + diasHabilitadosTexto + "."
              : ""}
          </Alert>
        ) : null}

        <Box sx={panelSx(COLOR.pedido)}>
          <TableContainer sx={{ bgcolor: "white" }}>
            <Table size="small" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellSx, textAlign: "left" }}>
                    Producto
                  </TableCell>
                  {semana.map((dia) => (
                    <TableCell
                      key={dia.fecha}
                      sx={{ ...headerCellSx, textTransform: "capitalize" }}
                    >
                      {dia.nombre} {dia.dia}
                    </TableCell>
                  ))}
                  <TableCell sx={headerCellSx}>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  ["butano", "Butano", totalButano],
                  ["propano", "Propano", totalPropano],
                ].map(([producto, titulo, total]) => (
                  <TableRow key={producto}>
                    <TableCell sx={tituloFilaSx}>{titulo}</TableCell>
                    {semana.map((dia) => (
                      <TableCell key={dia.fecha} sx={bodyCellSx}>
                        <TextField
                          size="small"
                          value={valores[dia.fecha]?.[producto] ?? ""}
                          disabled={!puedeEditar || cargando || guardando}
                          onChange={(e) =>
                            cambiarValor(dia.fecha, producto, e.target.value)
                          }
                          inputProps={{
                            inputMode: "numeric",
                            "aria-label": `${titulo} ${dia.dia}`,
                            style: {
                              textAlign: "center",
                              paddingTop: 5,
                              paddingBottom: 5,
                            },
                          }}
                          sx={{ width: 72 }}
                        />
                      </TableCell>
                    ))}
                    <TableCell sx={{ ...bodyCellSx, fontWeight: 700 }}>
                      {total}
                    </TableCell>
                  </TableRow>
                ))}

                <TableRow>
                  <TableCell sx={{ ...tituloFilaSx, color: "text.secondary" }}>
                    Asignados
                  </TableCell>
                  {semana.map((dia) => {
                    const butano = Math.max(obtenerAsignados(dia.fecha, "butano"), 0);
                    const propano = Math.max(obtenerAsignados(dia.fecha, "propano"), 0);

                    return (
                      <TableCell
                        key={dia.fecha}
                        sx={{ ...bodyCellSx, color: "text.secondary", fontSize: 12 }}
                      >
                        {butano || propano ? `B ${butano} · P ${propano}` : "-"}
                      </TableCell>
                    );
                  })}
                  <TableCell
                    sx={{ ...bodyCellSx, color: "text.secondary", fontSize: 12 }}
                  >
                    B {asignadosButano} · P {asignadosPropano}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box
          sx={{
            ...panelSx(COLOR.resumen),
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            columnGap: 2,
            rowGap: 1,
          }}
        >
          <Typography variant="body2">
            Pendientes de asignar: <b>Butano {totalButano - asignadosButano}</b> ·{" "}
            <b>Propano {totalPropano - asignadosPropano}</b>
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          <Button
            size="small"
            disabled={!hayCambios || guardando}
            onClick={() => {
              setValores(valoresOriginales);
              setErrorGuardado(null);
            }}
          >
            Deshacer cambios
          </Button>
          <Button
            size="small"
            variant="contained"
            color="success"
            disabled={!puedeEditar || !hayCambios || cargando || guardando}
            onClick={guardarSemana}
          >
            {guardando ? "Guardando..." : "Guardar"}
          </Button>
        </Box>

        {errorGuardado ? (
          <Alert severity="error">
            {errorGuardado.mensaje}
            {errorGuardado.problemas.length > 0 ? (
              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
                {errorGuardado.problemas.map((problema) => (
                  <li key={problema}>{problema}</li>
                ))}
              </Box>
            ) : null}
          </Alert>
        ) : null}
      </Box>

      <Snackbar
        open={Boolean(mensaje)}
        autoHideDuration={2500}
        onClose={() => setMensaje("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled" onClose={() => setMensaje("")}>
          {mensaje}
        </Alert>
      </Snackbar>
    </>
  );
}

export default SolicitudSucursal;